<template>
  <div class="default-layout">
    <overlay v-if="overlayActive" />
    <loader />
    <div
      id="viewport"
      :class="{checkout: isCheckoutPage, account: isAccountPage}"
      class="w-100 relative"
    >
      <main-header />
      <async-sidebar
        :async-component="SearchPanel"
        :is-open="isSearchPanelOpen"
        @close="$store.commit('ui/setSearchpanel')"
      />
      <async-sidebar
        :async-component="Microcart"
        :is-open="isMicrocartOpen"
        @close="$store.commit('ui/setMicrocart')"
      />
      <async-sidebar
        :async-component="SidebarMenu"
        :is-open="isSidebarOpen"
        direction="left"
        @close="$store.commit('ui/setSidebar')"
      />
      <async-sidebar
        :async-component="Wishlist"
        :is-open="isWishlistOpen"
        @close="$store.commit('ui/setWishlist')"
      />
      <slot />
      <main-footer />
      <notification />
      <sign-up />
      <offline-badge />
      <cookie-notification />
      <gdpr-cookie-setup-popup :gdpr-cookie-setup-data="getCookieSetupData" />
      <login-popup />
      <order-confirmation
        v-if="loadOrderConfirmation"
        :orders-data="ordersData"
      />
    </div>
    <vue-progress-bar />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import AsyncSidebar from 'theme/components/theme/blocks/AsyncSidebar/AsyncSidebar.vue'
import MainHeader from 'theme/components/core/blocks/Header/Header.vue'
import MainFooter from 'theme/components/core/blocks/Footer/Footer.vue'
import Overlay from 'theme/components/core/Overlay.vue'
import Loader from 'theme/components/core/Loader.vue'
import Notification from 'theme/components/core/Notification.vue'
import SignUp from 'theme/components/core/blocks/Auth/SignUp.vue'
import OfflineBadge from 'theme/components/core/OfflineBadge.vue'
import { isServer } from '@vue-storefront/core/helpers'
import Head from 'theme/head'
import config from 'config'
import CurrentPage from 'theme/mixins/currentPage'
import CookieNotification from 'theme/components/core/CookieNotification.vue'
import GdprCookieSetupPopup from 'theme/components/theme/blocks/GdprCookie/GdprCookieSetupPopup.vue'
import { CookieUserStorage } from 'src/modules/gdpr-cookie-setup/mixin/cookieUserStorage'
import { COOKIE_DATA_GETTER } from 'src/modules/gdpr-cookie-setup'
import LoginPopup from 'theme/components/core/LoginPopup.vue'
import { getMenuCategoriesCache } from '@vue-storefront/core/scripts/utils/menu-categories-cache'
import { CATEGORY_SET_MENU_LEAVES_PRUNED } from '@vue-storefront/core/modules/catalog-next/store/category/mutation-types'
import { Logger } from '@vue-storefront/core/lib/logger'

const SidebarMenu = () => import(/* webpackPreload: true */ /* webpackChunkName: "vsf-sidebar-menu" */ 'theme/components/core/blocks/SidebarMenu/SidebarMenu.vue')
const Microcart = () => import(/* webpackPreload: true */ /* webpackChunkName: "vsf-microcart" */ 'theme/components/core/blocks/Microcart/Microcart.vue')
const Wishlist = () => import(/* webpackPreload: true */ /* webpackChunkName: "vsf-wishlist" */ 'theme/components/core/blocks/Wishlist/Wishlist.vue')
const SearchPanel = () => import(/* webpackChunkName: "vsf-search-panel" */ 'theme/components/core/blocks/SearchPanel/SearchPanel.vue')
const OrderConfirmation = () => import(/* webpackChunkName: "vsf-order-confirmation" */ 'theme/components/core/blocks/Checkout/OrderConfirmation.vue')

const MENU_LEAVES_IDLE_TIMEOUT_MS = 3000

export default {
  components: {
    MainHeader,
    MainFooter,
    SidebarMenu, // eslint-disable-line vue/no-unused-components
    Overlay,
    Loader,
    Notification,
    SignUp,
    OfflineBadge,
    OrderConfirmation,
    AsyncSidebar,
    CookieNotification,
    GdprCookieSetupPopup,
    LoginPopup
  },
  mixins: [CurrentPage, CookieUserStorage],
  data () {
    return {
      loadOrderConfirmation: false,
      ordersData: [],
      Microcart,
      Wishlist,
      SearchPanel,
      SidebarMenu
    }
  },
  computed: {
    ...mapState({
      overlayActive: state => state.ui.overlay,
      isSearchPanelOpen: state => state.ui.searchpanel,
      isSidebarOpen: state => state.ui.sidebar,
      isMicrocartOpen: state => state.ui.microcart,
      isWishlistOpen: state => state.ui.wishlist
    }),
    ...mapGetters({
      getCookieSetupData: COOKIE_DATA_GETTER
    })
  },
  beforeMount () {
    // Progress bar on top of the page
    this.$router.beforeEach((to, from, next) => {
      this.$Progress.start()
      this.$Progress.increase(40)

      if (window.wiser) { window.wiser.resetNotif(window.location.href) }

      next()
    })
    this.$router.afterEach((to, from) => {
      this.$Progress.finish()
    })
    this.$bus.$on('offline-order-confirmation', this.onOrderConfirmation)
  },
  async mounted () {
    this.loadPrunedMenuLeaves()
    await this.applyStoredConsent()
  },
  beforeDestroy () {
    this.$bus.$off('offline-order-confirmation', this.onOrderConfirmation)
  },
  methods: {
    onOrderConfirmation (payload) {
      this.loadOrderConfirmation = true
      this.ordersData = payload
      this.$bus.$emit('modal-show', 'modal-order-confirmation')
    },
    menuLevels () {
      const { categoriesDynamicPrefetchLevel, menuLeafLevels = [] } = config.entities.category
      const levels = [].concat(categoriesDynamicPrefetchLevel)

      return {
        leafLevels: levels.filter(level => menuLeafLevels.includes(level)),
        fullLevels: levels.filter(level => !menuLeafLevels.includes(level))
      }
    },
    fetchMenuLevels (level, includeFields) {
      // On the server every render starts with an empty store, so the menu is read from a cache that
      // lives on the Express app instead of being fetched from the API for every uncached page.
      const serverCache = isServer && this.$ssrContext && this.$ssrContext.server
        ? getMenuCategoriesCache(this.$ssrContext.server.app)
        : null

      return this.$store.dispatch('category-next/fetchMenuCategories', {
        level,
        includeFields,
        excludeFields: config.entities.category.menuExcludeFields,
        skipCache: true,
        serverCache
      })
    },
    fetchMenuData () {
      if (!config.entities.category.categoriesDynamicPrefetch) {
        return this.fetchMenuLevels(null)
      }

      // The leaf levels (artists, venues) are ~1100 categories, so they are fetched with only the
      // fields the storefront reads from them.
      const { leafLevels, fullLevels } = this.menuLevels()
      const { menuLeafIncludeFields } = config.entities.category

      return Promise.all([
        fullLevels.length ? this.fetchMenuLevels(fullLevels) : null,
        leafLevels.length ? this.fetchMenuLevels(leafLevels, menuLeafIncludeFields) : null
      ])
    },
    /**
     * The SSR state only carries the leaf categories the page refers to (core/scripts/utils/prune-menu-state.ts).
     * The rest (search suggestions, the sidebar menu, tiles of products loaded later) is fetched once
     * the browser is idle.
     */
    loadPrunedMenuLeaves () {
      if (!this.$store.state['category-next'].menuLeavesPruned) {
        return
      }

      const load = () => {
        const { leafLevels } = this.menuLevels()

        this.fetchMenuLevels(leafLevels, config.entities.category.menuLeafIncludeFields)
          .then(() => this.$store.commit(`category-next/${CATEGORY_SET_MENU_LEAVES_PRUNED}`, false))
          .catch(err => Logger.error(err, 'menu')())
      }

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(load, { timeout: MENU_LEAVES_IDLE_TIMEOUT_MS })
      } else {
        setTimeout(load, MENU_LEAVES_IDLE_TIMEOUT_MS)
      }
    }
  },
  serverPrefetch () {
    this.$store.dispatch('magentoConfig/list', { path: null })
    return this.fetchMenuData()
  },
  metaInfo: Head
}
</script>

<style lang="scss" src="theme/css/main.scss"></style>
